/**
 * The Daily Report's two views, both drawn from the same `DailyReport` (domain/report.ts):
 * the formatted view (port of the original reportHTML) and the plain text (exactly buildReport's output).
 */
import { Fragment } from 'react';
import type { MetricKind } from '@/types/models';
import { fmtH, fmtNum } from '@/domain/format';
import { reportNames, type DailyReport } from '@/domain/report';
import { statusClass } from '@/domain/projects';
import { StatusBadge } from '@/ui/kit';

/** Duration in the formatted view: "30 min" under an hour, otherwise "2.5 h". */
export const reportDur = (hours: unknown): string => {
  const h = Number(hours) || 0;
  return h < 1 ? `${Math.round(h * 60)} min` : fmtH(h);
};

/** Kind tags on statistics: only "That day only" and "To date" metrics are tagged. */
const KIND_TAG: Partial<Record<MetricKind, string>> = { daily: 'Today', todate: 'To date' };

const weekdayDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export function ReportFormatted({ report: r }: { report: DailyReport }) {
  if (!r.general.length && !r.projects.length) return <div className="empty">No records found.</div>;
  return (
    <div className="dr">
      <div className="dr-head">
        <div className="dr-kicker">{weekdayDate(r.date)}</div>
        <div className="dr-title">{r.title}</div>
        {r.projects.length > 1 ? (
          <div className="dr-toc no-print">
            <span className="hint">Jump to:</span>
            {r.projects.map((x, i) => (
              <button key={x.project.id} type="button" onClick={() => document.getElementById(`dr-p${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                {x.project.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {r.general.length ? (
        <div className="dr-card dr-mgmt">
          <div className="dr-label mgmt">Management</div>
          {r.general.map((x) => (
            <div className="dr-row" key={x.memberId}>
              <div className="who">{x.name}</div>
              <div>
                <span className="dr-hrs">{reportDur(x.hours)}</span>
              </div>
              <div className="what">Worked on the projects</div>
            </div>
          ))}
        </div>
      ) : null}
      {r.projects.map((x, i) => (
        <section className={`dr-card ${statusClass(x.project.status)}`} id={`dr-p${i}`} key={x.project.id} aria-label={x.project.name}>
          <div className="dr-ch">
            <div className="row" style={{ gap: 8 }}>
              <h3>{x.project.name}</h3>
              <StatusBadge status={x.project.status} />
            </div>
            <span className="dr-sum">
              {x.work.length ? `${reportDur(x.hours)} · ${x.people} ${x.people === 1 ? 'person' : 'people'}` : 'No work logged'}
              {x.metrics.length ? ` · ${x.metrics.length} statistic${x.metrics.length === 1 ? '' : 's'}` : ''}
            </span>
          </div>
          {x.work.length ? (
            <>
              <div className="dr-label work">Work done</div>
              {x.work.map((g) => (
                <div className="dr-row" key={g.memberIds.join(',')}>
                  <div className="who">{reportNames(g.names)}</div>
                  <div>
                    <span className="dr-hrs">{reportDur(g.hours)}</span>
                  </div>
                  <div className="what">{g.work.length ? g.work.join('; ') : <span className="muted">—</span>}</div>
                </div>
              ))}
            </>
          ) : null}
          {x.note ? (
            <>
              <div className="dr-label note">Update</div>
              <div className="dr-note">{x.note}</div>
            </>
          ) : null}
          {x.metrics.length ? (
            <>
              <div className="dr-label stats">Statistics</div>
              <div className="dr-stats">
                {x.metrics.map((m, j) => (
                  <div className="dr-stat" key={j}>
                    <div className="k">
                      <span>{m.label}</span>
                      {m.kind && KIND_TAG[m.kind] ? <span className="dr-kind">{KIND_TAG[m.kind]}</span> : null}
                    </div>
                    <div className="v">{fmtNum(m.value)}</div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </section>
      ))}
    </div>
  );
}

/** The plain text as it will be copied; lines that are exactly a project name are shown in bold. */
export function ReportPlainText({ text, projectNames }: { text: string; projectNames: string[] }) {
  if (!text) return <>No records found.</>;
  const names = new Set(projectNames);
  return (
    <>
      {text.split('\n').map((line, i) => (
        <Fragment key={i}>
          {i ? '\n' : null}
          {/^\S/.test(line) && names.has(line) ? <b>{line}</b> : line}
        </Fragment>
      ))}
    </>
  );
}
